import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { PublicOffer, venueImageUrl } from '../api';
import { publicValidity } from './partner/shared';
import { categoryLabel, formatDays, formatDiscount } from '../features';
import FitImage from './FitImage';
import { PROPOSE_UI_COLORS } from '../theme';

const HIGHLIGHT_COUNT = 3;
const PLACEHOLDER_BG = `radial-gradient(circle at 70% 35%, ${PROPOSE_UI_COLORS.pink}, transparent 35%), linear-gradient(135deg, #1a1f52, #0a0e1a 65%, #46122a)`;

function OfferHighlightCard({ offer, claimLink, venueSlug }: { offer: PublicOffer; claimLink?: string; venueSlug: string }) {
  const [showTerms, setShowTerms] = useState(false);
  const cover = venueImageUrl(offer.coverImageUrl);
  return (
    <Box
      component="article"
      sx={{
        display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden',
        background: PROPOSE_UI_COLORS.surface,
        border: offer.isFeatured ? `1px solid ${PROPOSE_UI_COLORS.primary}` : '1px solid rgba(245,246,248,0.1)',
        boxShadow: offer.isFeatured ? '0 0 24px rgba(245,197,66,0.15)' : 'none'
      }}
    >
      <Box sx={{ position: 'relative', height: 150, background: PLACEHOLDER_BG }}>
        {cover ? <FitImage src={cover} alt={offer.title} /> : (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
            <LocalOfferIcon sx={{ fontSize: 44, color: 'rgba(255,255,255,0.55)' }} />
          </Box>
        )}
        <Box sx={{ position: 'absolute', top: 10, left: 10, right: 10, display: 'flex', justifyContent: 'space-between', gap: 1, zIndex: 1 }}>
          {formatDiscount(offer.discountType, offer.discountValue) ?? offer.benefit
            ? <Chip label={formatDiscount(offer.discountType, offer.discountValue) ?? offer.benefit} size="small" sx={{ background: PROPOSE_UI_COLORS.primary, color: '#000', fontWeight: 700, maxWidth: '70%' }} />
            : <span />}
          {offer.isFeatured && <Chip label="FEATURED" size="small" sx={{ background: PROPOSE_UI_COLORS.pink, color: '#fff', fontWeight: 700 }} />}
        </Box>
      </Box>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
        <Typography fontSize={11} fontWeight={700} color="text.secondary" letterSpacing={0.5}>{categoryLabel(offer.category).toUpperCase()}</Typography>
        <Typography fontWeight={700} fontSize={16} lineHeight={1.3}>{offer.title}</Typography>
        <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
          <AccessTimeIcon sx={{ fontSize: 14 }} />
          <Typography fontSize={12} color="text.secondary">{publicValidity(offer)}</Typography>
        </Box>
        {offer.description && <Typography fontSize={13} color="text.secondary">{offer.description}</Typography>}
        {offer.benefit && formatDiscount(offer.discountType, offer.discountValue) && <Typography fontSize={13} fontWeight={700}>{offer.benefit}</Typography>}
        <Typography fontSize={12} color="text.secondary">{formatDays(offer.activeDays)}</Typography>
        {offer.minimumSpend ? <Typography fontSize={12}>Minimum bill: ₹{offer.minimumSpend.toLocaleString('en-IN')}</Typography> : null}
        {offer.terms && (
          <>
            <Button size="small" onClick={() => setShowTerms((s) => !s)} aria-expanded={showTerms} sx={{ alignSelf: 'flex-start', p: 0, minWidth: 0, color: 'text.secondary', fontSize: 12, textTransform: 'none', textDecoration: 'underline' }}>
              {showTerms ? 'Hide T&C' : 'View T&C'}
            </Button>
            {showTerms && <Typography fontSize={12} color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{offer.terms}</Typography>}
          </>
        )}
        {offer.lastUpdatedUtc && (
          <Typography fontSize={11} color="text.secondary">
            Updated by venue {new Date(offer.lastUpdatedUtc).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </Typography>
        )}
        <Typography fontSize={11} color="text.secondary">
          Offers are run by the venue. <a style={{ color: 'inherit' }} href={`mailto:support@cheers2night.com?subject=${encodeURIComponent(`Incorrect offer: ${offer.title} (#${offer.id}, ${venueSlug})`)}`}>Report an expired or wrong offer</a>
        </Typography>
        {claimLink && <Button component={Link} to={claimLink} variant="contained" color="primary" sx={{ mt: 'auto', pt: 1 }} fullWidth>Claim Offer</Button>}
      </Box>
    </Box>
  );
}

// "Current Offers" on a venue page: offers as highlight cards (featured first, as the API sorts
// them), the first few up front and the rest behind "See all offers".
// canClaim: offer claiming is switched off on the server (discovery-only), so normally false.
const VenueOfferHighlights: React.FC<{ offers: PublicOffer[]; venueSlug: string; canClaim?: boolean }> = ({ offers, venueSlug, canClaim = false }) => {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? offers : offers.slice(0, HIGHLIGHT_COUNT);
  const hidden = offers.length - HIGHLIGHT_COUNT;

  return (
    <Box component="section" mb={5}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2} mb={2}>
        <Typography variant="h3" fontSize={22}>Current Offers <Typography component="span" color="text.secondary" fontSize={15}>({offers.length})</Typography></Typography>
        {hidden > 0 && (
          <Button size="small" onClick={() => setShowAll((s) => !s)} sx={{ color: PROPOSE_UI_COLORS.primary, fontWeight: 700 }}>
            {showAll ? 'Show fewer' : `See all offers (${offers.length})`}
          </Button>
        )}
      </Box>
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }} gap={2}>
        {shown.map((o) => <OfferHighlightCard key={o.id} offer={o} venueSlug={venueSlug} claimLink={canClaim ? `/offers/${venueSlug}/claim?offer=${o.id}` : undefined} />)}
      </Box>
      {hidden > 0 && !showAll && (
        <Box textAlign="center" mt={2}>
          <Button variant="outlined" onClick={() => setShowAll(true)} sx={{ borderColor: PROPOSE_UI_COLORS.primary, color: PROPOSE_UI_COLORS.primary }}>
            See all offers ({offers.length})
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default VenueOfferHighlights;
